import React from "react";
import { Card, CardBody } from "../../components/ui";
import PreviewChart from "./PreviewChart";
import logo from "../../assets/images/logo.svg";
import { User } from "lucide-react";

function AjoDashboardPreview({ preview, activeColor, rowIcons, fallbackRowIcon: FallbackRowIcon }) {
  const moduleColor = activeColor || preview.kpis[0].color;
  const timelineRows = preview.rows || [];

  return (
    <Card className="w-full overflow-hidden rounded-2xl border" style={{ borderColor: moduleColor, backgroundColor: 'white', boxShadow: '0 30px 60px -20px rgba(15,27,76,0.25)' }}>
      <style>{`
        @keyframes timelineCrawlIn {
          0% {
            opacity: 0;
            transform: translateY(14px) translateX(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) translateX(0);
          }
        }

        .animate-timeline-crawl {
          animation-name: timelineCrawlIn;
          animation-duration: 0.55s;
          animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
          animation-fill-mode: both;
        }
      `}</style>
      <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-4">
        <div className="flex gap-1.5">
          {["#ef4444", "#f59e0b", "#10b981"].map((c) => (
            <div key={c} className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c }} />
          ))}
        </div>
         <div className="mr-auto flex h-6 w-6 items-center justify-center rounded-full">
          <img src={logo} alt="Ajo Logo" className="mx-auto w-16 h-auto" />
        </div>
        <div className="ml-auto flex h-6 w-6 items-center justify-center rounded-full">
         <User size={16} strokeWidth={2.2} className="text-slate-400" />
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
          {preview.kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.label} className="rounded-xl border border-slate-200/80 bg-white shadow-none transition-colors hover:border-slate-300">
                <CardBody className="p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="truncate text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">{kpi.label}</p>
                    <div
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${kpi.color}14`, color: kpi.color }}
                    >
                      <Icon size={14} strokeWidth={2.2} />
                    </div>
                  </div>
                  <p className="text-[22px] font-semibold leading-none text-[#0D1330]">{kpi.value}</p>
                  <p className="mt-2 text-[11px] font-medium leading-snug text-slate-500">{kpi.sub}</p>
                </CardBody>
              </Card>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 lg:flex-nowrap">
          <div className="min-w-0 flex-1 basis-full bg-white lg:basis-3/5">
            <div className="p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Savings Growth Overview</p>
              <PreviewChart preview={preview} />
            </div>
          </div>
          <div className="min-w-0 flex-1 basis-full bg-white px-3 py-3 lg:basis-2/5">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent Activity</p>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: moduleColor }} />
            </div>
            <div className="relative pl-5">
              <div className="absolute left-2.5 top-0 h-full w-px bg-slate-200" />
              <div className="space-y-3">
                {timelineRows.map((row, i) => {
                  const RowIcon = rowIcons[row.icon] || FallbackRowIcon;
                  return (
                    <div
                      key={`${row.text}-${i}`}
                      className="animate-timeline-crawl flex items-start gap-3 rounded-md px-1 py-0.5"
                      style={{ animationDelay: `${i * 180}ms` }}
                    >
                      <div
                        className="relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center bg-white"
                        style={{ borderColor: moduleColor, color: moduleColor }}
                      >
                        <RowIcon size={14} strokeWidth={2.2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11.5px] leading-[1.4] text-[#0D1330]">{row.text}</p>
                        <p className="mt-0.5 text-[10.5px] text-slate-400">{row.meta}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default AjoDashboardPreview;
